import { FeatureFlagResponse } from '@commutifi/models/FeatureFlag';
import { BuildingProps } from '@commutifi/models/Buildings';
import { AVAILABLE_LANGUAGES } from '@commutifi-fe/constants';
import { CustomBranding, Account } from '@commutifi-fe/interfaces';
import { accountActions } from 'store/modules/accounts';
import { ExtendedAccount } from 'shared/interfaces/Account';
import { isFeatureEnabled as isFeatureEnabledRoot } from 'utils/helpers/featureFlags';
import { createContext, use } from 'react';
export type AccountContext = {
  id: string;
  lat: number | null | undefined;
  lng: number | null | undefined;
  currentAccount: ExtendedAccount;
  superAdminViewAccountId: string | null | undefined;
  currentLocaleConfig: (typeof AVAILABLE_LANGUAGES)[number] | undefined;
  firstName: string | undefined;
  lastName: string | null | undefined;
  refreshAccount: (promise?: {
    resolve: {
      (): Promise<void>;
      <T>(value: T | PromiseLike<T>): Promise<T>;
    };
    reject: <T = never>(reason?: any) => Promise<T>;
  } | undefined) => accountActions.FetchMeAction;
  building: Partial<BuildingProps>;
  accountWorksiteTimezone: string;
  /**
   * Whether the account is currently being fetched
   */
  isFetching: boolean;
  /**
   * Whether the account is currently being fetched and the account is not loaded at all yet
   * (we still need to fetch the account for the first time)
   */
  isLoading: boolean;
  error: Record<string, any> | undefined;
  logout: () => Record<string, any>;
  featureFlagsError: any;
  featureFlags: FeatureFlagResponse | null | undefined;
  isFeatureEnabled: ReturnType<typeof isFeatureEnabledRoot>;
  fetchFeatureFlagsForAccount: () => void;
  isLoadingFeatureFlags: boolean;
  updateAccount: (accountId: string, accountData: Partial<Account>) => Promise<ExtendedAccount>;
  updatePreferredLocale: (locale: string) => Promise<ExtendedAccount | undefined>;
  customBranding: CustomBranding | undefined;
};
export const CurrentAccountContext = createContext<AccountContext | null>(null);
export const useCurrentAccount = (opts?: {
  shouldThrow: boolean;
}) => {
  const context = use(CurrentAccountContext);
  if (!context) {
    if (opts?.shouldThrow === false) {
      return {} as AccountContext;
    }
    throw new Error('useCurrentAccount must be used within a CurrentAccountProvider');
  }
  return context;
};